import mithril from '../../vendor/mithril.js';
import {getGlobalBoundingClientRect} from '../dom.js'
import {dateEqual} from '../dateTime.js'

export const m = mithril


export function isObject(item) {
    return (item && typeof item === 'object' && !Array.isArray(item));
}

function assignDeep(target, ...sources) {
    const isObject = (value) => (value && typeof value === 'object' && !Array.isArray(value))
    if (!sources.length) return target
    const source = sources.shift()
    if (isObject(target) && isObject(source)) {
        for (const [key, value] of Object.entries(source)) {
            if (isObject(value)) {
                if (!target[key]) target[key] = {}
                assignDeep(target[key], value)
            } else {
                target[key] = source[key]
            }
        }
    }
    return assignDeep(target, ...sources)
}

export const Theme = {
    init: (...contents) => {
        assignDeep(Theme, ...contents)
    }
}

const sizeToClasses = {
    'tiny': 'text-xs px-2 py-1 rounded-md',
    'small': 'text-xs px-3 py-1.5 rounded-lg',
    'normal': 'text-sm px-4 py-2 rounded-xl',
    'large': 'w-80 text-base py-2.5 px-5 rounded-xl',
    'free': 'py-2 px-4 rounded-xl'
}

export class Control {
    constructor(vnode, classOrComponent) {
        this.classOrComponent = classOrComponent
        this.onVNodeChanged(vnode, {})
    }

    onVNodeChanged(vnode, old) {
        this.vnode = vnode || {}
        this.attrs = vnode.attrs || {}
    }

    getClassOrComponent() {
        return this.classOrComponent || 'div'
    }

    getAttributes() {
        const attrs = {}
        for(const [key, value] of Object.entries(this.attrs)) {
            const attributeName = key.charAt(0).toUpperCase() + key.slice(1)
            if (!this['get' + attributeName] && !this['on' + attributeName]) {
                attrs[key] = value
            }
        }
        return attrs
    }

    renderChildren() {
        return this.vnode.children
    }

    render() {
        const component = this.getClassOrComponent()
        return component && m(component, this.getAttributes(), this.renderChildren())
    }

    onbeforeupdate(vnode, old) {
        return this.onVNodeChanged(vnode, old)
    }

    view(vnode) {
        return this.render()
    }
}

export class ValueControl extends Control {
    constructor(vnode, classOrComponent) {
        super(vnode, classOrComponent)
    }

    onChange(newValue, oldValue) {
        return this.attrs.onChange && this.attrs.onChange(newValue, oldValue)
    }

    onVNodeChanged(vnode, old) {
        super.onVNodeChanged(vnode, old)
        if (this.attrs.hasOwnProperty('value')) {
            this.value = this.attrs.value
        }
    }

    isReadOnly() {
        return Boolean(this.attrs.readOnly)
    }

    getValue() {
        return this.value
    }

    valuesAreEqual(a, b) {
        return a === b
    }

    setValue(value) {
        if (!this.valuesAreEqual(this.value, value)) {
            this.value = value
            return true
        }
        return false
    }

    changeValue(newValue) {
        const oldValue = this.getValue()
        if (this.setValue(newValue)) {
            this.onChange(this.value, oldValue)
        }
    }
}

let popupIdCounter = 0

export class Popup extends Control {
    constructor(vnode) {
        super(vnode, '.relative')
        this.id = popupIdCounter++
        this.openPosition = [0, 0]
        this.listenToScroll = (ev) => {
            if (ev.target.id !== 'popup-panel-' + this.id) {
                this.close()
            }
        }
    }

    getTooltip() {
        return this.attrs.tooltip
    }

    onremove() {
        this.close()
    }

    isOpen() {
        return this.panelOpen
    }

    onOpen() {
        this.attrs.onOpen && this.attrs.onOpen(this)
    }

    onClose() {
        this.attrs.onClose && this.attrs.onClose(this)
    }

    setOpen(open) {
        if (open !== this.panelOpen) {
            if (!Popup.current && open) {
                this.panelOpen = true
                Popup.current = this
                document.addEventListener('scroll', this.listenToScroll, true)
                this.onOpen()
                m.redraw()
            } else if (!open) {
                this.panelOpen = false
                Popup.current = undefined
                document.removeEventListener('scroll', this.listenToScroll, true)
                this.onClose()
                m.redraw()
            }
        }
    }

    open() {
        this.setOpen(true)
    }

    close() {
        this.setOpen(false)
    }

    toggle() {
        this.setOpen(!this.isOpen())
    }

    getAttributes() {
        return Object.assign(super.getAttributes(), {
            id: 'popup-box-' + this.id,
            tabIndex: -1,
            onmouseover: (ev) => {
                if (this.getTooltip() && !this.isOpen()) {
                    this.tooltip = true
                    this.openPosition = [ev.clientX, ev.clientY]
                    this.open()
                }
            },
            onmouseout: () => {
                if (this.tooltip) {
                    this.close()
                    this.tooltip = false
                }
            },
            onclick: (ev) => {
                //ev.stopPropagation()
                this.openPosition = [ev.clientX, ev.clientY]
                if (this.tooltip) {
                    this.tooltip = false
                    this.open()
                } else {
                    this.toggle()
                }
            },
            onkeyup: (ev) => ev.code === 'Escape' && (this.close())
        })
    }

    getPanelWidth() {
        return this.attrs.panelWidth && (this.attrs.panelWidth + 'px')
    }

    getPanelHeight() {
        return this.attrs.panelHeight && (this.attrs.panelHeight + 'px')
    }

    getAnchorRect() {
        return getGlobalBoundingClientRect(document.getElementById('popup-box-' + this.id))
    }

    getOpenLeftByRect(rect, relative) {
        return this.getPanelLeftSource() === 'anchor' ?
            (relative ? 0 : rect.left) :
            (this.openPosition[0] - (relative ? rect.left : 0))
    }

    getOpenTopByRect(rect, relative) {
        return this.getPanelTopSource() === 'anchor' ?
            (relative ? rect.height : rect.bottom) :
            (this.openPosition[1] - (relative ? rect.top : 0))
    }

    getOpenPositionByRect(rect, relative) {
        return [this.getOpenLeftByRect(rect, relative), this.getOpenTopByRect(rect, relative)]
    }

    getOpenPosition(relative) {
        return this.getOpenPositionByRect(this.getAnchorRect(), relative)
    }

    getMaxHeightByRect(rect) {
        return Math.max(document.body.clientHeight - this.getOpenTopByRect(rect) - 20, 20)
    }

    shouldCloseOnBlur() {
        return this.attrs.closeOnBlur !== false
    }

    onRenderAnchor() {
        return this.attrs.renderAnchor && this.attrs.renderAnchor(this, this.getAttributes())
    }

    onRenderContent() {
        return this.attrs.renderContent && this.attrs.renderContent(this)
    }

    onRenderTooltip() {
        return this.attrs.renderTooltip && this.attrs.renderTooltip(this)
    }

    onPanelKeyUp(ev) {
        this.attrs.onPanelKeyUp && this.attrs.onPanelKeyUp(this, ev)
    }

    onPanelKeyDown(ev) {
        this.attrs.onPanelKeyDown && this.attrs.onPanelKeyDown(this, ev)
    }

    getTooltipTopSource() {
        return this.attrs.tooltipTopSource || 'anchor'
    }

    getTooltipLeftSource() {
        return this.attrs.tooltipLeftSource || 'anchor'
    }

    getPanelTopSource() {
        return this.tooltip ? this.getTooltipTopSource() : (this.attrs.panelTopSource || 'anchor')
    }

    getPanelLeftSource() {
        return this.tooltip ? this.getTooltipLeftSource() : (this.attrs.panelLeftSource || 'anchor')
    }

    renderMenu() {
        const rect = this.getAnchorRect()
        const pos = this.getOpenPositionByRect(rect)
        return m(
            'div', {
                id: 'popup-panel-' + this.id,
                class: 'fixed z-50 max-h-96 overflow-auto transition duration-200 ease-out bg-white bg-opacity-95 backdrop-blur-lg rounded-2xl shadow-2xl border border-slate-200 border-opacity-80 select-none outline-none focus:ring-2 focus:ring-brand1',
                style: {
                    width: this.getPanelWidth(),
                    height: this.getPanelHeight(),
                    left: pos[0] + 'px',
                    top: pos[1] + 'px',
                    maxHeight: this.getMaxHeightByRect(rect) + 'px'
                },
                tabIndex: this.isOpen() ? -1 : undefined,
                onkeydown: (ev) => this.onPanelKeyDown(ev),
                onkeyup: (ev) => {
                    if (ev.code === 'Escape') this.close()
                    this.onPanelKeyUp(ev)
                },
                onfocusout: (ev) => {
                    if (this.shouldCloseOnBlur() && !ev.currentTarget.contains(ev.relatedTarget))
                        setTimeout(() => {
                            this.close()
                            m.redraw()
                        }, 200)
                },
                oncreate: (vnode) => {
                    if(!this.tooltip) {
                        vnode.dom.focus()
                    }
                }
            },
            this.tooltip ? this.onRenderTooltip() : this.onRenderContent()
        )
    }

    render() {
        return [
            this.onRenderAnchor(),
            this.isOpen() && this.renderMenu()
        ]
    }
}

export class ContextMenu extends Popup {
    constructor(vnode) {
        super(vnode)
    }

    getMenuItems() {
        return (this.attrs.menuItems || []).filter(item => !!item)
    }

    isPreSelected(index) {
        return this.preSelectedIndex === index
    }

    preSelect(index) {
        this.preSelectedIndex = index
    }

    isSelectable(menuItem) {
        return menuItem && menuItem.action !== undefined
    }

    onPanelKeyUp(ev) {
        if (ev.code === 'Enter' && this.preSelectedIndex) {
            this.onPick(this.getMenuItems()[this.preSelectedIndex])
        }
    }

    onPick(menuItem) {
        if (menuItem && menuItem.action) menuItem.action(menuItem, this)
        this.close()
    }

    onRenderContent(select, close) {
        const items = this.getMenuItems()
        return this.tooltip ? super.onRenderContent() : (items.length > 0 ? m(
            'ul.bg-white.py-1.rounded-xl',
            items.map((menuItem, index) => {
                if (!menuItem) return
                const selectable = this.isSelectable(menuItem)
                const preSelected = this.isPreSelected(index)
                return m('li',
                    {
                        class: 'text-sm px-4 py-2 cursor-pointer transition-colors ' + (preSelected ? 'bg-brand1 text-white' : 'text-slate-700 hover:bg-slate-50'),
                        onclick: (ev) => {
                            //ev.stopPropagation()
                            this.onPick(menuItem)
                        },
                        onmouseenter: () => this.preSelect(selectable ? index : -1),
                    },
                    menuItem.label
                )
            })
        ) : [])
    }
}

const placeholders = {
    password: 'Passwort',
    email: 'E-Mail-Adresse'
}

const autocompletes = {
    password: 'current-password',
    email: 'email'
}

const inputClasses = 'rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-brand1 focus:ring-opacity-20 focus:border-brand1 transition-all'

export class Input extends ValueControl {
    constructor(vnode) {
        super(vnode, 'input')
    }

    getType() {
        return this.attrs.type || 'text'
    }

    getPlaceholder() {
        return this.attrs.placeholder || placeholders[this.getType()]
    }

    getAutocomplete() {
        return this.attrs.autocomplete || autocompletes[this.getType()]
    }

    valuesAreEqual(a, b) {
        const type = this.getType()
        if (type === 'date') {
            return dateEqual(a, b)
        } else if (type === 'file') {
            return false
        } else {
            return super.valuesAreEqual(a, b)
        }
    }

    applyEventValue(ev) {
        const type = this.getType()
        if (type === 'checkbox') {
            this.changeValue(ev.target.checked)
        } else if (type === 'date') {
            this.changeValue(new Date(ev.target.value))
        } else if (type === 'file') {
            this.changeValue(ev.target.files)
        } else {
            this.changeValue(ev.target.value)
        }
    }

    getAttributes() {
        const type = this.getType()
        const value = this.getValue()
        const baseAttrs = super.getAttributes()
        const attrs = Object.assign(baseAttrs, {
            type,
            autocomplete: this.getAutocomplete(),
            placeholder: this.getPlaceholder(),
            oninput: (ev) => this.applyEventValue(ev),
            onchange: (ev) => this.applyEventValue(ev),
            class: ((baseAttrs.class ? baseAttrs.class + ' ' : '') + inputClasses).trim()
        })
        if (type === 'checkbox') {
            attrs.oncreate = (vnode) => vnode.dom.checked = value
        } else if (type === 'date') {
            if (value instanceof Date) {
                const year = value.getFullYear()
                const month = value.getMonth() + 1
                const date = value.getDate()
                attrs.value = year + '-' + (month < 10 ? '0' : '') + month + '-' + (date < 10 ? '0' : '') + date
            } else {
                attrs.value = value
            }
        } else if (type === 'file') {
        } else {
            attrs.value = value
        }
        return attrs
    }
}

export class TextArea extends ValueControl {
    constructor(vnode) {
        super(vnode, 'textarea')
    }

    getPlaceholder() {
        return this.attrs.placeholder
    }

    getCols() {
        return this.attrs.cols || 50
    }

    getRows() {
        return this.attrs.rows || 2
    }

    getAttributes() {
        const baseAttrs = super.getAttributes()
        return Object.assign(baseAttrs, {
            placeholder: this.getPlaceholder(),
            cols: this.getCols(),
            rows: this.getRows(),
            value: this.getValue(),
            oninput: (ev) => this.changeValue(ev.target.value),
            class: ((baseAttrs.class ? baseAttrs.class + ' ' : '') + inputClasses).trim()
        })
    }
}

const kindToClasses = {
    'primary': 'bg-brand1 hover:bg-brand1-dark border border-transparent disabled:opacity-50 text-white shadow-sm hover:shadow transition-all duration-200',
    'secondary': 'bg-white hover:bg-slate-50 border border-slate-300 disabled:opacity-50 text-slate-700 shadow-sm transition-all duration-200',
    'danger': 'bg-danger hover:bg-red-600 border border-transparent disabled:opacity-50 text-white shadow-sm transition-all duration-200',
    'default': 'bg-slate-100 hover:bg-slate-200 border border-slate-200 disabled:opacity-50 text-slate-700 transition-all duration-200'
}

export class Button extends Control {
    constructor(vnode) {
        super(vnode, 'button')
    }

    getKind() {
        return this.attrs.kind || 'primary'
    }

    getSize() {
        return this.attrs.size || 'normal'
    }

    getAttributes() {
        const attrs = super.getAttributes()
        const baseClasses = 'font-semibold focus:outline-none inline-flex items-center justify-center'
        return Object.assign(attrs, {
            class: (
                baseClasses + ' ' +
                (attrs.class ? attrs.class + ' ' : '') +
                kindToClasses[this.getKind()] + ' ' +
                sizeToClasses[this.getSize()]
            ).trim()
        })
    }
}
export class Icon extends Control {
    constructor(vnode) {
        super(vnode, 'img')
    }

    getIconFace() {
        return this.attrs.iconFace
    }

    getSize() {
        return this.attrs.size || 24
    }

    getColor() {
        return this.attrs.color || (Theme.colors && Theme.colors.gray && Theme.colors.gray.DEFAULT) || '#64748b'
    }

    getOpacity() {
        return this.attrs.opacity || 1
    }

    getPixelSize() {
        const iconFace = this.getIconFace()
        return (this.getSize() || (iconFace && iconFace.size) || 24) + 'px'
    }

    getAttributes() {
        const iconFace = this.getIconFace()
        const pixelSize = this.getPixelSize()
        return Object.assign(super.getAttributes(), {
            src: iconFace && iconFace.getIconSrc(this.getColor(), this.getOpacity()),
            width: pixelSize,
            height: pixelSize,
        })
    }
}

export class IconButton extends Icon {
    getHighlightColor() {
        return this.attrs.highlightColor || Theme.colors.brand1.DEFAULT
    }

    getAttributes() {
        const iconFace = this.getIconFace()
        const attrs = super.getAttributes()
        return Object.assign(attrs, {
            class: ((attrs.class || '') + ' cursor-pointer').trim(),
            src: iconFace && iconFace.getIconSrc(
                this.highlight ? this.getHighlightColor() : this.getColor(), this.getOpacity()
            ),
            onmouseenter: () => this.highlight = true,
            onmouseleave: () => this.highlight = false,
        })
    }
}
